/**
 * 施工実績詳細ページ
 */

import React from "react";
import { getImageUrl } from "../../../utils/imageFetcher";
import "./ResultDetailPage.css";

const mediaUrl = (sp: string | undefined | null): string => (sp ? getImageUrl(sp) || "" : "");

interface ResultDetailPageProps {
  result: any;
  categories: any[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onBack: () => void;
}

// 日付をフォーマット（曜日付きJST）
function formatDateTimeJST(value: any): string {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const formatted = date.toLocaleString("ja-JP", options);
    const jstDate = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Tokyo" })
    );
    const weekday = weekdays[jstDate.getDay()];
    return formatted.replace(/日$/, `日(${weekday})`);
  } catch {
    return String(value);
  }
}

export const ResultDetailPage: React.FC<ResultDetailPageProps> = ({
  result,
  categories,
  onEdit,
  onDelete,
  onBack,
}) => {
  // カテゴリ名を取得
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || "未分類";
  };

  if (!result) {
    return (
      <div className="result-detail-page">
        <div className="empty-state">
          <p>施工実績が見つかりません</p>
          <button className="btn btn-primary" onClick={onBack}>
            一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  const mainImageStoragePath =
    result.mainImage?.storagePath ||
    result.mainImage?.storage_path ||
    result.main_image?.storagePath ||
    result.main_image?.storage_path;
  const galleryImages =
    result.galleryImages || result.gallery_images || [];
  const videos = result.videos || [];
  const clientInfo = result.clientInfo || result.client_info;

  return (
    <div className="result-detail-page">
      {/* ヘッダー */}
      <div className="result-detail-header">
        <h1>施工実績詳細</h1>
        <div className="result-detail-actions">
          <button className="btn btn-outline" onClick={onBack}>
            一覧に戻る
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => onEdit(result.id)}
          >
            編集
          </button>
          <button
            className="btn btn-danger"
            onClick={() => onDelete(result.id)}
          >
            削除
          </button>
        </div>
      </div>

      <div className="result-detail-content">
        {/* メイン画像セクション */}
        {mainImageStoragePath && (
          <section className="detail-section">
            <h2 className="section-title">メイン画像</h2>
            <div className="main-image-container">
              <img
                src={mediaUrl(mainImageStoragePath)}
                alt={result.title}
                className="main-image"
              />
            </div>
          </section>
        )}

        {/* 基本情報セクション */}
        <section className="detail-section">
          <h2 className="section-title">基本情報</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>ID:</label>
              <span>{result.id}</span>
            </div>
            <div className="detail-item">
              <label>タイトル:</label>
              <span>{result.title}</span>
            </div>
            <div className="detail-item">
              <label>カテゴリ:</label>
              <span>
                {getCategoryName(result.categoryId || result.category_id)}
              </span>
            </div>
            <div className="detail-item">
              <label>公開状態:</label>
              <span
                className={`status-badge ${
                  result.isPublished ?? result.is_published
                    ? "published"
                    : "draft"
                }`}
              >
                {result.isPublished ?? result.is_published ? "公開" : "非公開"}
              </span>
            </div>
            <div className="detail-item">
              <label>表示優先度:</label>
              <span>
                {result.displayPriority ?? result.display_priority ?? 0}
              </span>
            </div>
          </div>
        </section>

        {/* 工事概要セクション */}
        <section className="detail-section">
          <h2 className="section-title">工事概要</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>発注者名:</label>
              <span>{result.clientName || result.client_name || "-"}</span>
            </div>
            <div className="detail-item">
              <label>敷地面積:</label>
              <span>{result.siteArea || result.site_area || "-"}</span>
            </div>
            <div className="detail-item">
              <label>建築面積:</label>
              <span>{result.buildingArea || result.building_area || "-"}</span>
            </div>
            <div className="detail-item">
              <label>延床面積:</label>
              <span>
                {result.totalFloorArea || result.total_floor_area || "-"}
              </span>
            </div>
            <div className="detail-item">
              <label>構造・階数:</label>
              <span>{result.structureType || result.structure_type || "-"}</span>
            </div>
          </div>
        </section>

        {/* 説明セクション */}
        {(result.description || "") && (
          <section className="detail-section">
            <h2 className="section-title">説明・詳細</h2>
            <div className="description-content">
              <pre>{result.description}</pre>
            </div>
          </section>
        )}

        {/* 発注企業情報セクション */}
        {clientInfo && (
          <section className="detail-section">
            <h2 className="section-title">発注企業情報</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <label>企業名:</label>
                <span>
                  {clientInfo.companyName || clientInfo.company_name || "-"}
                </span>
              </div>
              <div className="detail-item">
                <label>住所:</label>
                <span>{clientInfo.address || "-"}</span>
              </div>
              <div className="detail-item">
                <label>ウェブサイト:</label>
                <span>
                  {clientInfo.websiteUrl || clientInfo.website_url ? (
                    <a
                      href={clientInfo.websiteUrl || clientInfo.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {clientInfo.websiteUrl || clientInfo.website_url}
                    </a>
                  ) : (
                    "-"
                  )}
                </span>
              </div>
              {(clientInfo.description || "") && (
                <div className="detail-item full-width">
                  <label>企業説明:</label>
                  <span>{clientInfo.description}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ギャラリー画像セクション */}
        {galleryImages.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">ギャラリー画像</h2>
            <div className="sub-images-grid">
              {galleryImages.map((image: any, index: number) => {
                const sp = image?.storagePath || image?.storage_path;
                if (!sp) return null;
                return (
                  <div key={index} className="sub-image-item">
                    <img
                      src={mediaUrl(sp)}
                      alt={image?.caption || `ギャラリー ${index + 1}`}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* 動画セクション */}
        {videos.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">動画</h2>
            <div className="sub-images-grid">
              {videos.map((video: any, index: number) => {
                const sp = video?.storagePath || video?.storage_path;
                if (!sp) return null;
                return (
                  <div key={index} className="sub-image-item">
                    <video
                      src={mediaUrl(sp)}
                      controls
                      style={{ width: "100%" }}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* システム情報セクション */}
        <section className="detail-section">
          <h2 className="section-title">システム情報</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>作成日時:</label>
              <span>
                {formatDateTimeJST(result.createDate || result.create_date)}
              </span>
            </div>
            <div className="detail-item">
              <label>更新日時:</label>
              <span>
                {formatDateTimeJST(result.updateDate || result.update_date)}
              </span>
            </div>
            <div className="detail-item">
              <label>バージョン:</label>
              <span>{result.version || "-"}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
